import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../environment";
import { all_routes } from "../router/all_routes";
import { AuthContext } from "../../context/authcontext";
import AuthCardLayout from "./AuthCardLayout";
import { getAuthApiError } from "./authApiError";

const RESET_LINK_ERROR_KEYS = ["token_required", "invalid_token", "token_expired"];

const ResetPassword: React.FC = () => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const route = all_routes;
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (auth) {
      navigate(route.dealsDashboard, { replace: true });
      return;
    }
  }, [auth, navigate, route.dealsDashboard]);

  const { token: pathToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get("token") || "";

  let token = pathToken || queryToken;

  if (token?.startsWith("token=")) {
    token = token.substring(6);
  }

  if (token?.includes("/") || token?.includes("=")) {
    const parts = token.split(/[\/=]/);
    token = parts[parts.length - 1];
  }

  const cleanToken = token;

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [passwordVisibility, setPasswordVisibility] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const togglePasswordVisibility = (
    field: "newPassword" | "confirmPassword"
  ) => {
    setPasswordVisibility((p) => ({
      ...p,
      [field]: !p[field],
    }));
  };

  useEffect(() => {
    if (!cleanToken || cleanToken.trim() === "") {
      MySwal.fire({
        title: "Invalid Reset Link",
        text: "The password reset link is invalid or has expired. Please request a new one.",
        icon: "error",
        confirmButtonText: "Return to Login",
      }).then(() => navigate(route.login));
    } else {
    }
  }, [cleanToken, navigate, route.login]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const passwordPolicy =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!passwordPolicy.test(formData.newPassword)) {
      return MySwal.fire({
        title: "Weak Password",
        html:
          "Password must be at least <strong>8 characters</strong> long and include:<br/>" +
          "• One uppercase letter<br/>" +
          "• One number<br/>" +
          "• One special character",
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return MySwal.fire({
        title: "Passwords Don’t Match",
        text: "Please make sure both fields are identical.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `${API_BASE_URL}/reset-password/`,
        {
          new_password: formData.newPassword,
          token: cleanToken,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      await MySwal.fire({
        title: "Success",
        text: "Your password has been reset. Please log in.",
        icon: "success",
        confirmButtonText: "Back to Login",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      navigate(route.login, { replace: true });
    } catch (err: unknown) {
      setIsSubmitting(false);
      const { errorKey, title, message } = getAuthApiError(
        err,
        "Error",
        "Failed to reset password. Please try again."
      );
      const isLinkInvalid = !!errorKey && RESET_LINK_ERROR_KEYS.includes(errorKey);

      await MySwal.fire({
        title,
        text: message,
        icon: "error",
        confirmButtonText: isLinkInvalid ? "Request New Link" : "OK",
        allowOutsideClick: !isLinkInvalid,
        allowEscapeKey: !isLinkInvalid,
      });

      if (isLinkInvalid) {
        navigate(route.forgotPassword, { replace: true });
      }
    }
  };

  return (
    <AuthCardLayout
      icon="ti-shield-lock"
      title="Reset Password"
      subtitle="Choose a new password for your account."
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-3">
          <label className="form-label" htmlFor="reset-new-password">
            New Password
          </label>
          <div className="pass-group">
            <input
              id="reset-new-password"
              name="newPassword"
              type={passwordVisibility.newPassword ? "text" : "password"}
              className="form-control pass-input"
              value={formData.newPassword ?? ""}
              onChange={handleChange}
              placeholder="Enter new password"
              autoComplete="new-password"
              disabled={isSubmitting}
              required
            />
            <span
              className={`ti toggle-passwords ${
                passwordVisibility.newPassword ? "ti-eye" : "ti-eye-off"
              }`}
              onClick={() => togglePasswordVisibility("newPassword")}
            />
          </div>
          <small className="text-muted d-block mt-1">
            At least 8 characters, with one uppercase letter, one number and
            one special character.
          </small>
        </div>

        <div className="mb-4">
          <label className="form-label" htmlFor="reset-confirm-password">
            Confirm New Password
          </label>
          <div className="pass-group">
            <input
              id="reset-confirm-password"
              name="confirmPassword"
              type={passwordVisibility.confirmPassword ? "text" : "password"}
              className="form-control pass-input"
              value={formData.confirmPassword ?? ""}
              onChange={handleChange}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              disabled={isSubmitting}
              required
            />
            <span
              className={`ti toggle-passwords ${
                passwordVisibility.confirmPassword ? "ti-eye" : "ti-eye-off"
              }`}
              onClick={() => togglePasswordVisibility("confirmPassword")}
            />
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Changing password...
            </>
          ) : (
            <>
              <i className="ti ti-lock-check me-2" />
              Change Password
            </>
          )}
        </button>
      </form>
    </AuthCardLayout>
  );
};

export default ResetPassword;
